/**
 * Stable Node Registry
 * 
 * Manages UUID-based node IDs that persist through Y.js operations.
 * Tracks node lineage when nodes split or merge.
 */

import * as Y from '../../../../shared/yjs';
import { createFileLogger } from '../../../../utils/logger';
import { generateDocumentPrefix } from '../../../../utils/entityIdGenerator';
import { generateNodeId as publicGenerateNodeId } from '../doc/docApi';

const log = createFileLogger('STABLENODEREGISTRY_SERVICE');

export interface NodeInfo {
  uuid: string;
  type: string;
  parentUuid?: string; // Set when node splits from parent
  createdAt: number;
  attributes: Record<string, any>;
}

export class StableNodeRegistry {
  private registry: Map<string, NodeInfo> = new Map();
  private nodeToUuid: WeakMap<Y.XmlElement, string> = new WeakMap();
  private documentPrefixes: Map<string, string> = new Map(); // docId -> prefix
  
  /**
   * Set the document prefix from backend data (persisted docMiniId)
   */
  setDocumentPrefix(docId: string, docMiniId: string): void {
    this.documentPrefixes.set(docId, docMiniId);
    log.log('Set document prefix from backend:', { docId: docId.slice(-8), docMiniId });
  }
  
  /**
   * Get the document prefix - assumes it's already been set from backend
   */
  getDocumentPrefix(docId: string): string {
    const prefix = this.documentPrefixes.get(docId);
    if (!prefix) {
      log.warn('Document prefix not found for docId:', docId.slice(-8));
      // Fallback to generating one (shouldn't happen in normal flow)
      const fallbackPrefix = generateDocumentPrefix();
      this.documentPrefixes.set(docId, fallbackPrefix);
      return fallbackPrefix;
    }
    return prefix;
  }
  
  /**
   * Generate a stable abstract pattern ID for nodes with document prefix
   * Example: "1234-abc-123-xyz" (doc 1234, abstract pattern)
   */
  generateNodeId(_docId?: string): string {
    return publicGenerateNodeId();
  }
  
  /**
   * Register a Y.js element with a prefixed abstract ID
   */
  registerNode(element: Y.XmlElement, parentUuid?: string, docId?: string): string {
    // Check if already registered
    const existingUuid = this.nodeToUuid.get(element);
    if (existingUuid) {
      return existingUuid;
    }
    
    // Get or generate abstract pattern ID
    let uuid = element.getAttribute('id');
    // If missing or invalid, assign nanoid
    if (!uuid || uuid === 'undefined') {
      uuid = this.generateNodeId(docId);
      element.setAttribute('id', uuid);
    }
    
    // Ensure uuid is always a string at this point
    if (!uuid) {
      uuid = this.generateNodeId(docId);
      element.setAttribute('id', uuid);
    }
    
    // Store in registry
    const nodeInfo: NodeInfo = {
      uuid,
      type: element.nodeName,
      parentUuid,
      createdAt: Date.now(),
      attributes: this.getElementAttributes(element)
    };
    
    this.registry.set(uuid, nodeInfo);
    this.nodeToUuid.set(element, uuid);
    
    log.log('Registered node:', { uuid, type: nodeInfo.type, parentUuid });
    
    return uuid;
  }
  
  /**
   * Get node info by UUID
   */
  getNodeInfo(uuid: string): NodeInfo | undefined {
    return this.registry.get(uuid);
  }
  
  /**
   * Find Y.js element by UUID in a fragment
   */
  findElementByUuid(yxml: Y.XmlFragment, uuid: string): Y.XmlElement | null {
    for (let i = 0; i < yxml.length; i++) {
      const element = yxml.get(i);
      if (element instanceof Y.XmlElement) {
        if (element.getAttribute('id') === uuid) {
          return element;
        }
        
        // Recursively search children
        const found = this.findElementInChildren(element, uuid);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
  
  private findElementInChildren(element: Y.XmlElement, uuid: string): Y.XmlElement | null {
    for (let i = 0; i < element.length; i++) {
      const child = element.get(i);
      if (child instanceof Y.XmlElement) {
        if (child.getAttribute('id') === uuid) {
          return child;
        }
        const found = this.findElementInChildren(child, uuid);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
  
  /**
   * Handle node split - track lineage
   */
  handleNodeSplit(originalUuid: string, newElement: Y.XmlElement): string {
    const newUuid = this.registerNode(newElement, originalUuid);
    log.log('Node split tracked:', { originalUuid, newUuid });
    return newUuid;
  }
  
  /**
   * Handle node merge - preserve oldest UUID
   */
  handleNodeMerge(uuid1: string, uuid2: string, mergedElement: Y.XmlElement): string {
    const info1 = this.registry.get(uuid1);
    const info2 = this.registry.get(uuid2);
    
    // Keep the older UUID
    const keepUuid = (info1?.createdAt || 0) <= (info2?.createdAt || 0) ? uuid1 : uuid2;
    const discardUuid = keepUuid === uuid1 ? uuid2 : uuid1;
    
    mergedElement.setAttribute('id', keepUuid);
    this.nodeToUuid.set(mergedElement, keepUuid);
    
    // Mark discarded UUID as merged
    const discardedInfo = this.registry.get(discardUuid);
    if (discardedInfo) {
      discardedInfo.attributes.mergedInto = keepUuid;
    }
    
    log.log('Node merge tracked:', { kept: keepUuid, discarded: discardUuid });
    return keepUuid;
  }
  
  /**
   * Extract all attributes from element
   */
  private getElementAttributes(element: Y.XmlElement): Record<string, any> {
    const attrs: Record<string, any> = {};
    const attrNames = element.getAttributes();
    for (const [key, value] of Object.entries(attrNames)) {
      attrs[key] = value;
    }
    return attrs;
  }
  
  /**
   * Clear registry (for cleanup)
   */
  clear(): void {
    this.registry.clear();
    this.nodeToUuid = new WeakMap();
  }
}

// Global registry instance
export const stableNodeRegistry = new StableNodeRegistry(); 